import { useState } from 'react';

import { Button, Input, Select } from '@/components';
import { useAuthUser, useRoles, useTeamMemberInvite } from '@/hooks';
import { useTranslation } from 'react-i18next';

import styles from './TeamInvite.module.css';

interface TeamInviteProps {
  setMembers: React.Dispatch<React.SetStateAction<Api.Member[]>>;
  onSuccess: TypeAny;
}

interface FormDataProps {
  email?: string;
  name?: string;
  role?: string;
}

export function TeamInvite({ setMembers, onSuccess }: TeamInviteProps) {
  const [formData, setFormData] = useState<FormDataProps>({});
  const { t } = useTranslation();
  const user = useAuthUser();
  const ROLES = [
    {
      display: t('teamInvite.selectRole'),
      value: ''
    },
    ...useRoles(t)
  ];

  const { mutation: teamMemberInvite, isLoading: isInviting } = useTeamMemberInvite({
    ...(formData as TypeAny),
    teamId: user?.team?.team_id,
    onSuccess: (data: Api.Member) => {
      setMembers((prevState) => [...prevState, { ...data }]);
      setFormData({});
      onSuccess();
    }
  });

  const handleForm = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) =>
    setFormData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));

  const handleInvite = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formData.email) {
      teamMemberInvite();
    }
  };

  return (
    <form className={styles.teamInvite} onSubmit={handleInvite}>
      <Input
        label={t('teamInvite.name')}
        onChange={handleForm}
        disabled={isInviting}
        name="name"
        required
      />

      <Input
        label={t('teamInvite.email')}
        onChange={handleForm}
        disabled={isInviting}
        name="email"
        required
      />

      <Select
        label={t('teamInvite.role')}
        selected={formData.role}
        onChange={handleForm}
        disabled={isInviting}
        options={ROLES}
        name="role"
        required
      />

      <Button
        disabled={!formData.role || !formData.email || !formData.name}
        isLoading={isInviting}
        type="submit"
        theme="cta"
      >
        {t('teamInvite.cta')}
      </Button>
    </form>
  );
}
